import { Link } from "react-router-dom";
import type { CoverageTrendEntry } from "../types";

const W = 320;
const H = 64;
const PAD = 6;

/**
 * Coverage over recent runs, with the go/no-go of each run carried by the point
 * and a dashed tick where the spec snapshot changed.
 *
 * A sparkline, not a chart: the shape of the trend is the message, and clicking
 * through expands into Coverage.
 */
export default function CoverageSparkline({ entries }: { entries: CoverageTrendEntry[] }) {
  if (entries.length < 2) {
    return <p className="text-sm opacity-60">Not enough reduced runs yet to show a trend.</p>;
  }

  const x = (i: number) => PAD + (i * (W - 2 * PAD)) / (entries.length - 1);
  const y = (pct: number) => H - PAD - (Math.max(0, Math.min(100, pct)) / 100) * (H - 2 * PAD);
  const line = entries
    .map((e, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)} ${y(e.coverage_pct).toFixed(1)}`)
    .join(" ");
  const latest = entries[entries.length - 1];

  return (
    <div className="flex items-center gap-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full max-w-sm h-16 text-primary"
        preserveAspectRatio="none"
        role="img"
        aria-label={`Coverage over the last ${entries.length} runs, latest ${latest.coverage_pct.toFixed(1)} percent`}
      >
        {entries.map((e, i) =>
          i > 0 && e.version_boundary ? (
            <line
              key={`b-${e.run_id}`}
              x1={x(i)}
              x2={x(i)}
              y1={PAD}
              y2={H - PAD}
              className="stroke-base-content/30"
              strokeWidth={1}
              strokeDasharray="2 2"
            >
              <title>{`#${e.run_number}: spec version changed`}</title>
            </line>
          ) : null,
        )}
        <path d={line} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
        {entries.map((e, i) => (
          <circle
            key={e.run_id}
            cx={x(i)}
            cy={y(e.coverage_pct)}
            r={2.5}
            className={e.go_no_go === "no_go" ? "fill-error" : "fill-success"}
          >
            <title>{`#${e.run_number}: ${e.coverage_pct.toFixed(1)}% · ${e.go_no_go ?? "no decision"}`}</title>
          </circle>
        ))}
      </svg>
      <Link to="/trends" className="link link-hover text-sm whitespace-nowrap">
        Coverage →
      </Link>
    </div>
  );
}
